/**
 * Hand-history persistence (IndexedDB via `idb`).
 *
 * Every completed hand is stored with its matchId and per-match hand
 * sequence number alongside the full `HandRecord` (docs/UI_REFERENCE.md
 * "ハンド振り返り"). The history screen reads the most recent 100
 * entries; the detail modal reads a single entry by id.
 */
import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { HandRecord, PlayerIndex } from "@joker-holdem/core";
import { computeEvNetBb } from "./ev.js";

export interface StoredHand {
  /** Autoincrement primary key. */
  id: number;
  matchId: string;
  /** 1-based hand number within the match. */
  handIndex: number;
  timestamp: number;
  record: HandRecord;
  /** Hero's (seat 0) net chip result this hand -- denormalized for fast list rendering. */
  heroNetChips: number;
  /**
   * Hero's all-in-adjusted EV net result, in bb (see `history/ev.ts`).
   * Optional because rows saved before this field existed don't have it;
   * `backfillMissingEv` fills those in lazily when the history screen loads.
   */
  evNetBb?: number;
}

/**
 * A completed match's final result (2026-07-17 フィードバック #2). Saved the
 * moment a hand's completion is detected to have ended the match (i.e. when
 * `finishHand` sees `matchOver`), not when the player taps a button --
 * display of this data is a later feature, this just persists it.
 */
export interface StoredMatch {
  /** Same id used for the match's hands' `matchId` field. */
  matchId: string;
  endedAt: number;
  winnerSeat: PlayerIndex;
  /** Total hands played in the match (`MatchState.handNumber` after the final hand). */
  handCount: number;
  anteEnabled: boolean;
  finalStacks: readonly [number, number];
}

interface JokerHoldemDB extends DBSchema {
  hands: {
    key: number;
    value: StoredHand;
    indexes: { byTimestamp: number };
  };
  matches: {
    key: string;
    value: StoredMatch;
  };
}

const DB_NAME = "joker-holdem";
const DB_VERSION = 2;

/**
 * How long a caller waits for the DB connection before giving up FOR THAT
 * CALL. IndexedDB opens can hang indefinitely (most commonly: another tab
 * still holds an older-version connection, blocking our upgrade -- observed
 * on iOS Safari in the 2026-07-17 "次のハンドが反応しない" bug), and nothing
 * in the game may ever block on storage. The pending open is kept around so
 * a later call can still succeed if the blocker goes away.
 */
let openTimeoutMs = 2500;

let dbPromise: Promise<IDBPDatabase<JokerHoldemDB>> | null = null;
let warnedUnavailable = false;

function warnUnavailable(err: unknown): void {
  if (warnedUnavailable) return;
  warnedUnavailable = true;
  console.warn("[joker-holdem] ハンド履歴の保存領域(IndexedDB)を利用できません。ゲームは続行できますが履歴は記録されません。", err);
}

function getDb(): Promise<IDBPDatabase<JokerHoldemDB>> {
  if (!dbPromise) {
    dbPromise = openDB<JokerHoldemDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Guarded with `contains` (rather than keying off `oldVersion`) so
        // this works both for a fresh install (jumping straight to v2) and
        // an upgrade from a real v1 database that already has "hands".
        if (!db.objectStoreNames.contains("hands")) {
          const store = db.createObjectStore("hands", {
            keyPath: "id",
            autoIncrement: true,
          });
          store.createIndex("byTimestamp", "timestamp");
        }
        if (!db.objectStoreNames.contains("matches")) {
          db.createObjectStore("matches", { keyPath: "matchId" });
        }
      },
      blocked() {
        // Another tab holds an older-version connection; our open waits.
        // Callers are protected by the per-call timeout below.
        console.warn("[joker-holdem] 別のタブが古いバージョンの履歴DBを開いているため、待機しています。他のタブを閉じると解消します。");
      },
      blocking() {
        // WE are the old-version connection blocking a NEWER page elsewhere:
        // close so the other tab can upgrade; reopen lazily on next use.
        void dbPromise?.then((db) => db.close()).catch(() => {});
        dbPromise = null;
      },
      terminated() {
        dbPromise = null;
      },
    });
    // A failed open must not be cached forever -- reset so later calls retry.
    dbPromise.catch(() => {
      dbPromise = null;
    });
  }
  return dbPromise;
}

/**
 * Fail-soft connection getter: resolves to null (never throws, never hangs
 * beyond the timeout) when storage is unavailable. Game flow must always
 * treat persistence as best-effort.
 */
async function getDbSafe(): Promise<IDBPDatabase<JokerHoldemDB> | null> {
  try {
    const result = await Promise.race([
      getDb(),
      new Promise<null>((resolve) => {
        setTimeout(() => resolve(null), openTimeoutMs);
      }),
    ]);
    if (result === null) warnUnavailable("open timed out");
    return result;
  } catch (err) {
    warnUnavailable(err);
    return null;
  }
}

/** Persists a completed match's final result (best-effort, never throws). */
export async function saveMatchResult(match: StoredMatch): Promise<void> {
  const db = await getDbSafe();
  if (!db) return;
  try {
    await db.put("matches", match);
  } catch (err) {
    warnUnavailable(err);
  }
}

/** Test/dev helper: wipes all stored match results. */
export async function clearAllMatches(): Promise<void> {
  const db = await getDbSafe();
  if (!db) return;
  await db.clear("matches");
}

export async function getMatchResult(matchId: string): Promise<StoredMatch | undefined> {
  const db = await getDbSafe();
  if (!db) return undefined;
  try {
    return await db.get("matches", matchId);
  } catch (err) {
    warnUnavailable(err);
    return undefined;
  }
}

/**
 * Persists a completed hand. Returns the assigned autoincrement id, or null
 * when storage is unavailable (best-effort -- the game must proceed either
 * way; see the 2026-07-17 freeze bug note on `openTimeoutMs`).
 */
export async function saveHand(
  matchId: string,
  handIndex: number,
  record: HandRecord
): Promise<number | null> {
  const heroNetChips = record.netResult[0];
  const evNetBb = computeEvNetBb(record);
  const entry: Omit<StoredHand, "id"> = {
    matchId,
    handIndex,
    timestamp: Date.now(),
    record,
    heroNetChips,
    evNetBb,
  };
  const db = await getDbSafe();
  if (!db) return null;
  try {
    return await db.add("hands", entry as StoredHand);
  } catch (err) {
    warnUnavailable(err);
    return null;
  }
}

/** Most recent hands, newest first, capped at `limit` (default 100). Empty on storage failure. */
export async function getRecentHands(limit = 100): Promise<StoredHand[]> {
  const db = await getDbSafe();
  if (!db) return [];
  try {
    const tx = db.transaction("hands", "readonly");
    const index = tx.store.index("byTimestamp");
    const result: StoredHand[] = [];
    let cursor = await index.openCursor(null, "prev");
    while (cursor && result.length < limit) {
      result.push(cursor.value);
      cursor = await cursor.continue();
    }
    await tx.done;
    return result;
  } catch (err) {
    warnUnavailable(err);
    return [];
  }
}

/**
 * Every stored hand, oldest first, across all matches. Used to compute the
 * cumulative P/L series (docs/UI_REFERENCE.md "フィードバック反映"): the
 * running balance must carry over from match to match rather than resetting
 * to 0, so the graph's baseline for its displayed window has to be derived
 * from the full history, not just the most recent slice.
 */
export async function getAllHandsChronological(): Promise<StoredHand[]> {
  const db = await getDbSafe();
  if (!db) return [];
  try {
    const tx = db.transaction("hands", "readonly");
    const index = tx.store.index("byTimestamp");
    const result: StoredHand[] = [];
    let cursor = await index.openCursor(null, "next");
    while (cursor) {
      result.push(cursor.value);
      cursor = await cursor.continue();
    }
    await tx.done;
    return result;
  } catch (err) {
    warnUnavailable(err);
    return [];
  }
}

export async function getHandById(id: number): Promise<StoredHand | undefined> {
  const db = await getDbSafe();
  if (!db) return undefined;
  try {
    return await db.get("hands", id);
  } catch (err) {
    warnUnavailable(err);
    return undefined;
  }
}

/**
 * Fills in `evNetBb` for any rows saved before that field existed
 * (docs/UI_REFERENCE.md "フィードバック反映" #10), persisting the computed
 * value back to IndexedDB so this only runs once per row. Returns a new
 * array with every entry's `evNetBb` populated; entries that already had
 * it are returned unchanged (same object identity).
 */
export async function backfillMissingEv(hands: readonly StoredHand[]): Promise<StoredHand[]> {
  const db = await getDbSafe();
  const result: StoredHand[] = [];
  for (const h of hands) {
    if (h.evNetBb !== undefined) {
      result.push(h);
      continue;
    }
    const updated: StoredHand = { ...h, evNetBb: computeEvNetBb(h.record) };
    // Persist when possible; the in-memory value is returned either way so
    // the graph still gets a complete EV series without storage.
    if (db) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await db.put("hands", updated);
      } catch (err) {
        warnUnavailable(err);
      }
    }
    result.push(updated);
  }
  return result;
}

/** Test/dev helper: wipes all stored hands. */
export async function clearAllHands(): Promise<void> {
  const db = await getDbSafe();
  if (!db) return;
  await db.clear("hands");
}

/** Test-only: forces a fresh DB connection on next call (fake-indexeddb resets between test files). */
export function _resetDbConnectionForTests(): void {
  dbPromise = null;
  warnedUnavailable = false;
}

/** Test-only: shrinks the open timeout so failure-path tests run fast. */
export function _setOpenTimeoutForTests(ms: number): void {
  openTimeoutMs = ms;
}
